// Tasks resource — the canonical work-item collection. Replaces the
// old /api/queue projection. State changes still flow through
// /api/events (via useChangeTaskState in events.ts) so the engine can
// evaluate triggers; this file owns the read surface and the
// assignee/due_at PUT path.

import { queryOptions } from "@tanstack/react-query";

import { NULL_VALUE } from "~/lib/filters";
import { halFetch, type HalCollection, type HalResource } from "~/lib/hal";

/** Lifecycle states a task can be in.
 *
 * `pending` is a transient state: the backend has accepted an event
 * but the async queue consumer hasn't computed the resulting state
 * yet. The SPA renders it as in-flight feedback (disabled buttons +
 * tinted badge) until a subsequent refetch reflects the consumer's
 * write. Single-tab interactions never observe `pending` for tasks
 * they themselves mutated because the optimistic update overlays
 * the eventual state directly. Other tabs / clients will see it.
 *
 * See docs/technical-notes.md "Event Processing" for the why.
 */
export type TaskState =
  | "todo"
  | "in progress"
  | "complete"
  | "cancelled"
  | "rejected"
  | "delayed"
  | "pending"
  | "error";

/** The states a CLIENT may write, which is narrower than the states a row may
 *  be IN: "error" is the engine's alone, published by the server when work
 *  cannot proceed, and it holds across recomputes (TMF-407, TMF-452). */
const TASK_STATES: ReadonlySet<string> = new Set<TaskState>([
  "todo",
  "in progress",
  "complete",
  "cancelled",
  "rejected",
  "delayed",
  "pending",
]);

/** Narrow a wire (or event) string to a task state, or null when it is none of
 *  them. The union above is CLOSED, so this is where values from outside it are
 *  answered: the engine's event vocabulary is wider than a task's (it carries
 *  "un-cancel" and ""), and a caller writing one into a task row wants "not a
 *  state I can write" rather than a lie. "error" is excluded here for the same
 *  reason though a row may be in it: no client action produces one. */
export function asTaskState(value: string): TaskState | null {
  return TASK_STATES.has(value) ? (value as TaskState) : null;
}

/** Outcome of the task that produced a verdict (TMF-202): complete when it
 * reached a conclusion, error when it was blocked (an error verdict carries no
 * conclusion, only the detail). */
export type VerdictStatus = "complete" | "error";

/**
 * Extraction is one structured value the verdict's system task read off the
 * document (TMF-198) — the raw data behind the claim. `type` is the field-type
 * slug (e.g. "Name", "DOB"); `value` is the read text; `confidence` the optional
 * provenance. The review card renders these as its field rows (TMF-227).
 */
export interface Extraction {
  id: string;
  task_id: string;
  type: string;
  value: string;
  confidence?: number | null;
}

/**
 * Verdict is a system task's conclusion about one document question (TMF-198,
 * TMF-202). `tool` is the vision tool that produced it; `conclusion` is the bare
 * answer ("yes", "no", a date) taken verbatim from the tool (empty for an error
 * verdict); `detail` is the long-form explanation; `confidence` the optional
 * provenance. It's the content the human review card renders. `_embedded.extractions`
 * are the structured values that same system task read — a verdict and its
 * extractions share one system task id (TMF-227).
 */
export interface Verdict {
  id: string;
  task_id: string;
  tool: string;
  conclusion: string;
  confidence?: number | null;
  detail: string;
  status: VerdictStatus;
  _embedded?: {
    extractions?: Extraction[];
  };
}

/** The field type the case-match step writes its candidate projects under: one
 *  JSON extraction listing the Filevine projects a document's client could be
 *  on, written when the match couldn't be made automatically (TMF-370). It
 *  reaches the exception gate as an extraction of the verdict the gate renders,
 *  and is presented as pickable options — never as a field row. */
export const MATCH_CANDIDATES_FIELD_TYPE = "MatchCandidates";

/** The field type a Handoff writes its contact candidates under: one JSON
 *  extraction naming the person the push refused on and the Filevine contacts
 *  a rep picks between, empty on any run that refused on nobody (TMF-650). Like
 *  the case-match candidates it is a set of options, never a field row. */
export const CONTACT_CANDIDATES_FIELD_TYPE = "ContactCandidates";

export const HANDOFF_TASK_TYPE = "Handoff";
export const RESOLVE_CONTACT_TASK_TYPE = "Resolve the contact";
export const RETRY_HANDOFF_TASK_TYPE = "Retry the handoff";

/** One Filevine project offered at the exception gate: the id the gate is
 *  resolved with, plus the name and date of loss the human tells the candidates
 *  apart by. Mirrors the persisted extraction contract (consumers.matchCandidate);
 *  `incident_date` is an ISO date, empty when Filevine carries none. */
export interface MatchCandidate {
  project_id: number;
  project_name: string;
  incident_date: string;
}

function isMatchCandidate(value: unknown): value is MatchCandidate {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.project_id === "number" &&
    typeof candidate.project_name === "string" &&
    typeof candidate.incident_date === "string"
  );
}

/** The parsed value of the verdict's candidates extraction of this type, or
 *  undefined when the verdict carries none or its value is not JSON. */
function candidatesValue(verdict: Verdict, type: string): unknown {
  const row = (verdict._embedded?.extractions ?? []).find((e) => e.type === type);
  if (row === undefined) return undefined;
  try {
    return JSON.parse(row.value);
  } catch {
    return undefined;
  }
}

/** The candidate projects a gate's verdict carries, in the order the search
 *  found them. Absent or unreadable data reads as none, so the card falls back
 *  to the manual id entry that has always been there rather than failing to
 *  render the gate at all. */
export function matchCandidates(verdict: Verdict): MatchCandidate[] {
  const parsed = candidatesValue(verdict, MATCH_CANDIDATES_FIELD_TYPE);
  return Array.isArray(parsed) ? parsed.filter(isMatchCandidate) : [];
}

/** One Filevine contact a Handoff's search found for the person it refused
 *  on. Mirrors the persisted extraction contract; an empty list is a contact
 *  Filevine holds no phone or email for. */
export interface ContactCandidate {
  contact_id: number;
  full_name: string;
  person_types: string[];
  phones: string[];
  emails: string[];
}

function isStringList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isContactCandidate(value: unknown): value is ContactCandidate {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.contact_id === "number" &&
    typeof candidate.full_name === "string" &&
    isStringList(candidate.person_types) &&
    isStringList(candidate.phones) &&
    isStringList(candidate.emails)
  );
}

/** The person a Handoff refused on and the contacts its search found, in
 *  search order. Absent or unreadable data reads as nobody and none, which the
 *  card renders as a decision it cannot write. */
export function contactCandidates(verdict: Verdict): {
  personId: string | null;
  candidates: ContactCandidate[];
} {
  const none = { personId: null, candidates: [] };
  const parsed = candidatesValue(verdict, CONTACT_CANDIDATES_FIELD_TYPE);
  if (typeof parsed !== "object" || parsed === null) return none;
  const record = parsed as Record<string, unknown>;
  if (!Array.isArray(record.candidates)) return none;
  return {
    personId: typeof record.person_id === "string" && record.person_id !== "" ? record.person_id : null,
    candidates: record.candidates.filter(isContactCandidate),
  };
}

/** A Filevine person type as a rep reads it: the vendor spells its values in
 *  CamelCase ("MedicalProvider"), so a space goes where a lowercase letter meets
 *  a capital. A run of capitals ("PI") is one word and stays together. */
export function personTypeLabel(personType: string): string {
  return personType.replace(/(\p{Ll})(\p{Lu})/gu, "$1 $2");
}

/** The extractions the review card renders as "what the machine read" — the
 *  candidate sets are excluded because they are rendered as options. */
export function readFields(verdict: Verdict): Extraction[] {
  return (verdict._embedded?.extractions ?? []).filter(
    (e) =>
      e.type !== MATCH_CANDIDATES_FIELD_TYPE &&
      e.type !== CONTACT_CANDIDATES_FIELD_TYPE,
  );
}

/** One candidate's label: the case as the human recognizes it, then the id they
 *  would otherwise be typing, then the date of loss that tells same-name matters
 *  apart when Filevine carries one. */
export function matchCandidateLabel(candidate: MatchCandidate): string {
  const name = candidate.project_name.trim() || "(unnamed case)";
  const dol = candidate.incident_date.trim();
  return `${name} · ${candidate.project_id}${dol ? ` · DOL ${dol}` : ""}`;
}

/** Lifecycle of a Review — the approval primitive a review task carries. */
export type ReviewStatus = "Pending" | "Approved" | "Rejected";

/**
 * Review is the approval record for a human review task (TMF-198), embedded on
 * the task under `_embedded.review`. It links the review task to the Verdict it
 * gates on (embedded under its own `_embedded.verdict`) and records the
 * resolution (status + the resolving user). Created Pending when the review
 * task unlocks; the SPA renders the card off its verdict and drives
 * approve/reject through the task's state.
 */
export interface Review {
  id: string;
  task_id: string;
  verdict_id: string;
  user_id?: string | null;
  status: ReviewStatus;
  _embedded?: {
    verdict?: Verdict;
  };
}

/** Minimal case reference embedded under a task's project, so a queue row can
 *  link back to its case detail page (/cases/:id) and label itself. Absent for
 *  case-less projects. */
export interface TaskProjectCase {
  id: string;
  name: string;
}

/** Minimal batch reference embedded under a task's project, for the queue rows
 *  whose project is a scanned drop rather than a matter. */
export interface TaskProjectDocumentUpload {
  id: string;
  name: string;
}

/**
 * The project a task belongs to, embedded on the task. Tasks are
 * project-scoped now (TMF-170), and the name a row displays is the subtype's
 * own column (TMF-512), so both subtypes ride nested under the project.
 */
export interface TaskProject {
  id: string;
  _embedded?: {
    case?: TaskProjectCase;
    document_upload?: TaskProjectDocumentUpload;
  };
}

/** The document a task is subjected to, embedded under a per-document task
 *  (documents.id = tasks.document_id). The Document Uploads exceptions hub reads
 *  it off the `set filevine project id` gate to label the flagged document. */
export interface TaskDocument {
  id: string;
  upload_project_id: string;
  type: string;
  name: string;
}

/**
 * A task row from /api/tasks. The FK relations preload under HAL's
 * `_embedded` envelope per the goat convention; UUID-only
 * `project_id` is included as the relation's FK column for filtering/PUT.
 */
export type Task = HalResource<{
  id: string;
  project_id: string;
  user_id?: string;
  name: string;
  type: string;
  role: string;
  /** true → manually completed (the case-detail "Mark done" button).
   *  false → implicitly completed via mutation of some other resource
   *  (e.g. logging an expense, attaching a record). */
  is_manual: boolean;
  state: TaskState;
  // due_at is the workflow-relative deadline as Go time.Duration —
  // serialized as nanoseconds (int64). Kept on the type for callers
  // that want the raw duration; display uses due_date.
  due_at?: number | null;
  // due_date is the materialized absolute deadline (created_at + due_at),
  // computed by the backend repo on every write. ISO 8601 timestamp.
  // Use this for rendering and for any due-window filter params.
  due_date?: string | null;
  completed_at?: string | null;
  created_at: string;
  updated_at?: string | null;
  _embedded: {
    /** The project this task belongs to — carries the display name and
     *  (nested) the case the queue row links to. `TaskEmbeds.Project` has
     *  no `omitempty`, so an unresolved project arrives as explicit null. */
    project?: TaskProject | null;
    /** For a human review task, the Review it carries — and, nested, the
     *  Verdict it gates on. The review card renders off this (TMF-198). The
     *  document-uploads `set filevine project id` gate carries its Review the
     *  same way — the `review case match` verdict (the reason it needs a human). */
    review?: Review;
    /** The document a per-document task is subjected to (the exceptions hub
     *  reads it off the gate). */
    document?: TaskDocument;
    /** A system task's own conclusion, on the case read: why a Handoff
     *  stopped or what it pushed. */
    verdict?: Verdict;
  };
}>;

/** Filter shape for tasksListQueryOptions. Mirrors the API params. */
export interface TasksFilter {
  user_id?: string;
  project_id?: string;
  role?: string;
  /** Repeated-key multi-value: each entry becomes its own `state=` param
   *  (?state=todo&state=in+progress). Server treats the values as
   *  exact-match OR. */
  state?: TaskState[];
  /** One task type, exact-match (TMF-512). The board reads its deadline rows
   *  by type, because a matter's list row carries no plan to find them in. */
  type?: string;
  /** goat's page-size param. A caller that reads a type across the whole firm
   *  says how much of it fits in one page; the default is goat's. */
  size?: number;
}

/** The document-uploads exception gate: the human `set filevine project id`
 *  task the system leaves in ToDo when it can't auto-file a document. The
 *  exceptions hub lists this one type across every upload project. A stable
 *  task-TYPE contract value (not a constructed per-document task name). */
export const SET_FILEVINE_PROJECT_ID_TASK_TYPE = "set filevine project id";

/** The human review-gate task types: the ones that carry a Verdict and are
 *  resolved by an Approve / Reject decision. The document-uploads exception gate
 *  (SET_FILEVINE_PROJECT_ID_TASK_TYPE) carries a verdict too but collects a case
 *  id instead, so it is deliberately NOT in this set. Read by the TaskTable (to
 *  decide which rows grow a ReviewCard) and by the upload review queue (to find
 *  the open gate a case-attached document is waiting on). */
export const REVIEW_GATE_TASK_TYPES: ReadonlySet<string> = new Set([
  "review identity verdict",
  "review minor status verdict",
  "review split verdict",
]);

/** The verdict a gate task gates on, if its Review has landed yet. A gate task
 *  appears a beat before its review + verdict rows do, and no gate card can
 *  render without one. */
export function gateVerdict(task: Task): Verdict | undefined {
  return task._embedded?.review?._embedded?.verdict;
}

/** A review gate the human still has to answer: a review-gate type, not already
 *  terminal, and carrying the verdict its card renders. `pending` counts as open
 *  — the engine is mid-transition, and the card renders read-only rather than
 *  vanishing out from under the reviewer. */
export function isOpenReviewGate(task: Task): boolean {
  if (!REVIEW_GATE_TASK_TYPES.has(task.type)) return false;
  if (task.state === "complete" || task.state === "rejected") return false;
  return gateVerdict(task) !== undefined;
}

/** A document's `set filevine project id` exception gate AWAITING A HUMAN, or
 *  undefined when nothing is waiting on one. This — the TASK state — is what
 *  makes an upload document reviewable. Deriving that from the document's
 *  metadata instead is the bug this replaced (TMF-371): a document whose
 *  matched_project_id landed but whose gate completion did not looked resolved,
 *  so the review pane hid the very gate that repairs it. `todo` is that wedge
 *  shape, and it is what must stay visible.
 *
 *  `pending` is NOT awaiting a human: the completion the reviewer just submitted
 *  is accepted and in flight at the engine. Unlike isOpenReviewGate — where a
 *  pending gate keeps rendering read-only so the card doesn't vanish
 *  mid-transition — re-presenting a pending exception gate offers work already
 *  done, and answering it a second time wedges the queue (TMF-371).
 *
 *  The excluded states are the terminal pair the API refuses to complete
 *  (`cancelled` is terminal alongside `complete` — core.StateIsCompletedOrCancelled
 *  — and `rejected` is a human's terminal answer) plus in-flight `pending`, so
 *  the card is offered only where the server would accept an answer. */
export function openMatchGate(
  tasks: Task[],
  documentId: string,
): Task | undefined {
  return tasks.find(
    (task) =>
      task.type === SET_FILEVINE_PROJECT_ID_TASK_TYPE &&
      task._embedded.document?.id === documentId &&
      task.state !== "complete" &&
      task.state !== "cancelled" &&
      task.state !== "rejected" &&
      task.state !== "pending" &&
      gateVerdict(task) !== undefined,
  );
}

/** "me" sentinel resolves server-side to the authenticated user's ID. */
export const ASSIGNEE_ME = "me";

/** The shared null filter value, which matches rows with no assignee: the
 *  department-pool / unassigned set. */
export const ASSIGNEE_NULL = NULL_VALUE;

export type TasksCollection = HalCollection<Task>;

export const TASKS_LIST_KEY = ["tasks"] as const;
export const tasksListQueryKey = (filter: TasksFilter) =>
  [...TASKS_LIST_KEY, filter] as const;

export const tasksListQueryOptions = (filter: TasksFilter) =>
  queryOptions<TasksCollection>({
    queryKey: tasksListQueryKey(filter),
    queryFn: ({ signal }) => {
      // sort=created_at&dir=asc flips the API's newest-first default to
      // queue order — the queue page wants the oldest open work at the
      // top, and freshly-toggled rows must keep their position on the
      // post-mutation refetch rather than jumping.
      const params = new URLSearchParams({ sort: "created_at", dir: "asc" });
      if (filter.user_id !== undefined) {
        params.set("user_id", filter.user_id);
      }
      if (filter.project_id !== undefined) params.set("project_id", filter.project_id);
      if (filter.role !== undefined) params.set("role", filter.role);
      if (filter.type !== undefined) params.set("type", filter.type);
      if (filter.size !== undefined) params.set("size", String(filter.size));
      if (filter.state !== undefined) {
        for (const s of filter.state) params.append("state", s);
      }
      return halFetch<TasksCollection>(`/api/tasks?${params.toString()}`, { signal });
    },
    staleTime: 30 * 1000,
  });

/** The home queue is "my open work" — assignee=me, state in (todo, in progress). */
export const myOpenTasksFilter: TasksFilter = {
  user_id: ASSIGNEE_ME,
  state: ["todo", "in progress"],
};

export const taskQueryKey = (id: string) => [...TASKS_LIST_KEY, id] as const;

/** One task with its embeds (project, document, review→verdict) —
 *  GET /api/tasks/:id carries the same preloads as the list. */
export const taskQueryOptions = (id: string) =>
  queryOptions<Task>({
    queryKey: taskQueryKey(id),
    queryFn: ({ signal }) => halFetch<Task>(`/api/tasks/${id}`, { signal }),
  });
